import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { GroupPrediction, Prediction, UserPredictions, KnockoutMatch, Group } from '../types/tournament';

interface PredictionContextType {
  groupPredictions: GroupPrediction[];
  knockoutPredictions: Prediction[];
  defaultGroupStandings: Map<string, string[]>;
  setDefaultGroupStandings: (groups: Group[]) => void;
  setGroupPrediction: (groupId: string, standings: string[]) => void;
  setKnockoutPrediction: (matchId: string, winnerId: string) => void;
  getGroupStandings: (groupId: string) => string[];
  getKnockoutWinner: (matchId: string) => string | undefined;
  resolveTeamSource: (source: string, knockoutMatches: KnockoutMatch[]) => string | undefined;
  clearAllPredictions: () => void;
}

const PredictionContext = createContext<PredictionContextType | undefined>(undefined);

const STORAGE_KEY = 'wc2026-predictions';

function loadPredictions(): UserPredictions {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load predictions:', e);
  }
  return { groupPredictions: [], knockoutPredictions: [] };
}

function savePredictions(predictions: UserPredictions) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(predictions));
  } catch (e) {
    console.error('Failed to save predictions:', e);
  }
}

export function PredictionProvider({ children }: { children: ReactNode }) {
  const [groupPredictions, setGroupPredictions] = useState<GroupPrediction[]>([]);
  const [knockoutPredictions, setKnockoutPredictions] = useState<Prediction[]>([]);
  const [defaultGroupStandings, setDefaultGroupStandingsState] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    const loaded = loadPredictions();
    setGroupPredictions(loaded.groupPredictions);
    setKnockoutPredictions(loaded.knockoutPredictions);
  }, []);

  useEffect(() => {
    savePredictions({ groupPredictions, knockoutPredictions });
  }, [groupPredictions, knockoutPredictions]);

  const setDefaultGroupStandings = useCallback((groups: Group[]) => {
    const map = new Map<string, string[]>();
    groups.forEach(group => {
      map.set(group.id, group.teams);
    });
    setDefaultGroupStandingsState(map);
  }, []);

  const setGroupPrediction = useCallback((groupId: string, standings: string[]) => {
    setGroupPredictions(prev => {
      const filtered = prev.filter(p => p.groupId !== groupId);
      return [...filtered, { groupId, standings }];
    });
  }, []);

  const setKnockoutPrediction = useCallback((matchId: string, winnerId: string) => {
    setKnockoutPredictions(prev => {
      const filtered = prev.filter(p => p.matchId !== matchId);
      return [...filtered, { matchId, winnerId }];
    });
  }, []);

  const getGroupStandings = useCallback((groupId: string): string[] => {
    const prediction = groupPredictions.find(p => p.groupId === groupId);
    if (prediction?.standings) {
      return prediction.standings;
    }
    // Return default standings from group data if no prediction exists
    return defaultGroupStandings.get(groupId) || [];
  }, [groupPredictions, defaultGroupStandings]);

  const getKnockoutWinner = useCallback((matchId: string): string | undefined => {
    const prediction = knockoutPredictions.find(p => p.matchId === matchId);
    return prediction?.winnerId;
  }, [knockoutPredictions]);

  const resolveTeamSource = useCallback((source: string, knockoutMatches: KnockoutMatch[]): string | undefined => {
    // Handle group position sources like "1A", "2B", "3ABCDF"
    if (source.match(/^[123][A-L]+$/)) {
      const position = parseInt(source[0]);
      const groups = source.slice(1);

      // For 3rd place teams with multiple groups (e.g., "3ABCDF")
      if (position === 3 && groups.length > 1) {
        // For default, pick first available third place team from the group options
        for (const group of groups.split('')) {
          const standings = getGroupStandings(group);
          if (standings.length >= 3) {
            return standings[2]; // 3rd place (0-indexed)
          }
        }
        return undefined;
      }

      // Simple case: single group like "1A", "2B", "3A"
      const group = groups[0];
      const standings = getGroupStandings(group);
      if (standings.length >= position) {
        return standings[position - 1];
      }
      return undefined;
    }

    // Handle knockout winner sources like "W73", "W89"
    if (source.startsWith('W')) {
      const matchNum = parseInt(source.slice(1));
      const matchId = `M${matchNum}`;
      return getKnockoutWinner(matchId);
    }

    // Handle loser sources like "L101" for 3rd place match
    if (source.startsWith('L')) {
      const matchNum = parseInt(source.slice(1));
      const matchId = `M${matchNum}`;
      const winner = getKnockoutWinner(matchId);
      if (!winner) return undefined;

      // Find the match and return the other team
      const match = knockoutMatches.find(m => m.id === matchId);
      if (!match) return undefined;

      const team1 = resolveTeamSource(match.team1Source, knockoutMatches);
      const team2 = resolveTeamSource(match.team2Source, knockoutMatches);

      return winner === team1 ? team2 : team1;
    }

    return undefined;
  }, [getGroupStandings, getKnockoutWinner]);

  const clearAllPredictions = useCallback(() => {
    setGroupPredictions([]);
    setKnockoutPredictions([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <PredictionContext.Provider
      value={{
        groupPredictions,
        knockoutPredictions,
        defaultGroupStandings,
        setDefaultGroupStandings,
        setGroupPrediction,
        setKnockoutPrediction,
        getGroupStandings,
        getKnockoutWinner,
        resolveTeamSource,
        clearAllPredictions,
      }}
    >
      {children}
    </PredictionContext.Provider>
  );
}

export function usePredictions() {
  const context = useContext(PredictionContext);
  if (!context) {
    throw new Error('usePredictions must be used within a PredictionProvider');
  }
  return context;
}
